import { useState, useRef, useEffect, useCallback } from "react"
import { useDraggable, useDndMonitor } from "@dnd-kit/core"
import { Popover, Tooltip } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTriangleExclamation, faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import { getEventCardColors } from "../scheduleEventColorsModel"
import { SpanningEventSegment, MONTH_VIEW_SPANNING_ROW_HEIGHT, SPANNING_EVENT_GAP, MONTH_VIEW_DAY_HEADER_HEIGHT } from "./spanningEventHelpers"
import { DateRangeViewEvent } from "../scheduleApi"
import { RESCHEDULED_EVENT_STRIPE_PATTERN, HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"
import { EventDisplayInfo, isCrewEditable, isEventDragDisabled } from "../scheduleModel"
import EventStatusIcon from "../EventStatusIcon"
import AvatarGroup from "../DayView/AvatarGroup"
import EventSelectCrew from "../EventSelectCrew"
import EventPopoverContent from "../EventPopoverContent"

export type ResizePreview = {
  eventId: number
  previewEndDate: Dayjs
  weekIndex: number
  rowIndex: number
} | null

export type Props = {
  segment: SpanningEventSegment
  eventDisplayInfo: Map<number, EventDisplayInfo>
  onEventClick: (eventId: number) => void
  onResize?: (event: DateRangeViewEvent, newEndDate: Dayjs) => void
  onResizingChange?: (isResizing: boolean) => void
  onResizePreview?: (preview: ResizePreview) => void
  weekStart: Dayjs
}

const MonthViewSpanningEvent = ({ segment, eventDisplayInfo, onEventClick, onResize, onResizingChange, onResizePreview, weekStart }: Props) => {
  const { event, startDayIndex, spanWidth, isStart, isEnd, rowIndex, weekIndex } = segment
  const [isHovering, setIsHovering] = useState(false)
  const [resizeTargetEpoch, setResizeTargetEpoch] = useState<number | null>(null)
  const [elementWidth, setElementWidth] = useState<number>(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dayWidth = useRef<number>(0)
  const justFinishedResizing = useRef(false)
  const resizeTargetEpochRef = useRef<number | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const showAvatars = elementWidth >= 120

  const displayInfo = eventDisplayInfo.get(event.id)
  const dragDisabled = isEventDragDisabled(event, displayInfo)

  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `spanning-event-${event.id}-${weekIndex}`,
    data: { event, segment },
    disabled: dragDisabled,
  })

  const resizeId = `spanning-event-${event.id}-${weekIndex}-resize`
  const {
    attributes: resizeAttributes,
    listeners: resizeListeners,
    setNodeRef: setResizeNodeRef,
    isDragging: isResizing,
  } = useDraggable({
    id: resizeId,
    data: { event, segment, isResize: true },
    disabled: dragDisabled,
  })

  const setRefs = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node)
    containerRef.current = node

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
    }

    if (node) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (entry) {
          setElementWidth(entry.contentRect.width)
        }
      })
      resizeObserverRef.current.observe(node)
      setElementWidth(node.offsetWidth)
    }
  }, [setNodeRef])

  useEffect(() => {
    if (containerRef.current) {
      const parentElement = containerRef.current.parentElement
      if (parentElement) {
        dayWidth.current = parentElement.offsetWidth / 7
      }
    }
  })

  useEffect(() => {
    onResizingChange?.(isResizing)
  }, [isResizing, onResizingChange])

  useDndMonitor({
    onDragOver: (dragOverEvent) => {
      if (dragOverEvent.active.id !== resizeId) {
        return
      }
      const overDate = dragOverEvent.over?.data.current?.date as Dayjs | undefined
      if (overDate) {
        const epoch = overDate.valueOf()
        resizeTargetEpochRef.current = epoch
        setResizeTargetEpoch(epoch)
      }
    },
    onDragEnd: (dragEvent) => {
      if (dragEvent.active.id !== resizeId) {
        return
      }
      justFinishedResizing.current = true
      onResizePreview?.(null)

      const targetEpoch = resizeTargetEpochRef.current
      if (onResize && targetEpoch !== null) {
        const targetDate = dayjs(targetEpoch)
        const eventStart = dayjs(event.start)
        if (!targetDate.isBefore(eventStart, 'day') && !targetDate.isSame(dayjs(event.end), 'day')) {
          onResize(event, targetDate)
        }
      }

      resizeTargetEpochRef.current = null
      setResizeTargetEpoch(null)
    },
  })

  const { backgroundColor, palette } = getEventCardColors(event.crewForemanColor)

  const leftPercent = (startDayIndex / 7) * 100
  let widthPercent = (spanWidth / 7) * 100

  let resizingPastWeekEnd = false

  if (isResizing && resizeTargetEpoch !== null) {
    const targetDate = dayjs(resizeTargetEpoch)
    const eventStart = dayjs(event.start)
    const targetDayIndex = targetDate.diff(weekStart.startOf('day'), 'day')
    const minEndDayIndex = Math.max(startDayIndex, eventStart.diff(weekStart.startOf('day'), 'day'))
    const newEndDayIndex = Math.max(targetDayIndex, minEndDayIndex)

    resizingPastWeekEnd = newEndDayIndex > 6

    const clampedEndDayIndex = Math.min(newEndDayIndex, 6)
    const newSpanWidth = clampedEndDayIndex - startDayIndex + 1
    widthPercent = (newSpanWidth / 7) * 100
  }

  useEffect(() => {
    if (!isResizing || resizeTargetEpoch === null) {
      return
    }
    const targetDate = dayjs(resizeTargetEpoch)
    const targetDayIndex = targetDate.diff(weekStart.startOf('day'), 'day')

    if (targetDayIndex > 6) {
      onResizePreview?.({
        eventId: event.id,
        previewEndDate: targetDate,
        weekIndex,
        rowIndex,
      })
    } else {
      onResizePreview?.(null)
    }
  }, [isResizing, resizeTargetEpoch])

  const top = MONTH_VIEW_DAY_HEADER_HEIGHT + rowIndex * (MONTH_VIEW_SPANNING_ROW_HEIGHT + SPANNING_EVENT_GAP)

  const paddingLeft = isStart ? 2 : 0
  const paddingRight = isEnd ? 2 : 0

  const isRescheduled = event.rescheduledToEventId != null

  const style: React.CSSProperties = {
    position: "absolute",
    top,
    left: `calc(${leftPercent}% + ${paddingLeft}px)`,
    width: `calc(${widthPercent}% - ${paddingLeft + paddingRight}px)`,
    height: MONTH_VIEW_SPANNING_ROW_HEIGHT,
    display: "flex",
    alignItems: "stretch",
    gap: 4,
    padding: "4px 8px",
    borderRadius: isStart && isEnd ? 4 : isStart ? "4px 0 0 4px" : isEnd ? "0 4px 4px 0" : 0,
    backgroundColor,
    color: palette.primaryText,
    cursor: isResizing ? "ew-resize" : isDragging ? "grabbing" : "pointer",
    overflow: "hidden",
    boxSizing: "border-box",
    zIndex: isDragging || isResizing ? 100 : 1,
    transform: !isResizing && transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    backgroundImage: isRescheduled ? RESCHEDULED_EVENT_STRIPE_PATTERN : undefined,
  }

  const crewEditable = isCrewEditable(displayInfo)

  const dragProps = isResizing ? {} : { ...listeners, ...attributes }

  return (
    <Popover
      content={
        <EventPopoverContent
          event={event}
          workareaStatuses={eventDisplayInfo.get(event.id)?.workareaStatuses}
          timesheets={eventDisplayInfo.get(event.id)?.timesheets}
          warning={eventDisplayInfo.get(event.id)?.warning ?? undefined}
          rescheduledToDate={eventDisplayInfo.get(event.id)?.rescheduledToDate}
        />
      }
      open={isHovering && !isDragging && !isResizing}
      placement="bottom"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setRefs}
        {...dragProps}
        onClick={(e) => {
          e.stopPropagation()
          if (justFinishedResizing.current) {
            justFinishedResizing.current = false
            return
          }
          if (!isResizing) {
            onEventClick(event.id)
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          if (!isResizing) {
            setIsHovering(false)
          }
        }}
        style={style}
      >
        {!isStart && (
          <span style={{ color: palette.secondaryText, fontSize: 10, flexShrink: 0, alignSelf: 'center' }}>◀</span>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {displayInfo && showAvatars && (
              <EventStatusIcon status={displayInfo.status} />
            )}
            {event.frozen && (
              <Tooltip title="Frozen">
                <FontAwesomeIcon icon={faSnowflake} style={{ color: palette.secondaryText, fontSize: 12, flexShrink: 0 }} />
              </Tooltip>
            )}
            <span
              style={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
                textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined,
              }}
            >
              {event.customer?.displayName}
            </span>
            {showAvatars && (
              <div style={{ flexShrink: 0 }}>
                {crewEditable
                  ? <EventSelectCrew event={event} variant="avatar" color={palette.primaryText} />
                  : <AvatarGroup crewForemanEmail={event.crewForemanEmail} crewMemberIds={event.crewMemberIds} initialsColor={palette.primaryText} />
                }
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
            {displayInfo?.warning && (
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#fa8c16', fontSize: 14, flexShrink: 0 }} />
            )}
            <span
              style={{
                color: palette.secondaryText,
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined,
              }}
            >
              {event.jobName}
            </span>
          </div>
          <span style={{ color: palette.secondaryText, fontSize: 12, whiteSpace: 'nowrap' }}>All Day</span>
        </div>
        {!isEnd && !isResizing && !resizingPastWeekEnd && (
          <span style={{ color: palette.secondaryText, fontSize: 10, flexShrink: 0, alignSelf: 'center' }}>▶</span>
        )}

        {isEnd && (isHovering || isResizing) && onResize && (
          <div
            ref={setResizeNodeRef}
            {...resizeListeners}
            {...resizeAttributes}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 8,
              cursor: "ew-resize",
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 3,
                height: 12,
                backgroundColor: palette.primaryText,
                borderRadius: 2,
              }}
            />
          </div>
        )}
      </div>
    </Popover>
  )
}

export default MonthViewSpanningEvent
